import {
  Users,
  UserCheck,
  Network,
  Settings2,
  HelpCircle,
  MessageCircleQuestion,
  ClipboardCheck,
} from 'lucide-react';
import { useAuth } from '../../auth';
import { useNavigation } from '../../context/NavigationContext';
import { usePendingApprovalsCount } from '../../hooks/usePendingApprovalsCount';
import type { TabType } from './Navigation';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface MoreMenuItem {
  id: TabType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  show?: 'always' | 'manager' | 'hr' | 'staff';
}

const MORE_ITEMS: MoreMenuItem[] = [
  { id: 'manager', label: 'Team-Übersicht', icon: Users, show: 'manager' },
  { id: 'approvals', label: 'Genehmigungen', icon: ClipboardCheck, show: 'manager' },
  { id: 'hr', label: 'Mitarbeiter', icon: UserCheck, show: 'hr' },
  { id: 'orgchart', label: 'Organigramm', icon: Network, show: 'hr' },
  { id: 'hr-settings', label: 'HR-Einstellungen', icon: Settings2, show: 'hr' },
  { id: 'help', label: 'Hilfe', icon: HelpCircle },
  { id: 'faq', label: 'FAQ', icon: MessageCircleQuestion },
];

interface MoreMenuProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (tab: TabType) => void;
}

export function MoreMenu({ open, onClose, onNavigate }: MoreMenuProps) {
  const { activeTab } = useNavigation();
  const { isHR, isManager } = useAuth();
  const pendingCount = usePendingApprovalsCount();

  const visibleItems = MORE_ITEMS.filter((item) => {
    if (item.show === 'manager') return isManager;
    if (item.show === 'hr') return isHR;
    if (item.show === 'staff') return isHR || isManager;
    return true;
  });

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="bottom"
        className="lg:hidden rounded-t-xl safe-area-bottom pb-20 [&>button.absolute]:hidden"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Weitere Navigation</SheetTitle>
        </SheetHeader>
        <div className="space-y-1" role="menu">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                role="menuitem"
                className={cn(
                  'flex items-center gap-3 w-full px-3 py-3 rounded-md text-sm transition-colors',
                  isActive
                    ? 'font-medium bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-50'
                    : 'text-gray-600 dark:text-gray-400 active:bg-gray-100 dark:active:bg-gray-800'
                )}
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
                {item.id === 'approvals' && pendingCount > 0 && (
                  <span className="ml-auto min-w-[1.25rem] h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-5 text-center">
                    {pendingCount > 9 ? '9+' : pendingCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
